import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { type NotificationDto } from "@keydials/api-client";
import { notificationHref } from "./notificationHref";
import { api, getAccessToken } from "../../auth/AuthContext";
import { onNotify, onReconnected, type RealtimeEvent } from "../../realtime/connection";
import { Icon } from "../Icon";

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "";
  }
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * The header bell: lists the user's notifications, shows an unread badge, marks read, and deep-links.
 * Each row has a "Done" tick that dismisses it (KEY-710) — read means seen, done means dealt with and gone.
 */
export function NotificationCentre() {
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [open, setOpen] = useState(false);

  const reload = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      setItems(await api.listNotifications(token));
    } catch {
      // best-effort; the bell just won't update
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Real-time arrival: the hub auto-joins the user's group on connect, so we just react to "notification".
  useEffect(() => {
    const offNotify = onNotify((e: RealtimeEvent) => {
      if (e.type === "notification") {
        void reload();
      }
    });
    const offReconnect = onReconnected(() => void reload());
    return () => {
      offNotify();
      offReconnect();
    };
  }, [reload]);

  const unread = useMemo(() => items.filter((i) => !i.isRead).length, [items]);

  async function openItem(n: NotificationDto) {
    setOpen(false);
    const token = getAccessToken();
    if (!n.isRead) {
      setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, isRead: true } : i)));
      if (token) {
        try {
          await api.markNotificationRead(token, n.id);
        } catch {
          // ignore; optimistic
        }
      }
    }
    navigate(notificationHref(n));
  }

  async function markAll() {
    const token = getAccessToken();
    setItems((prev) => prev.map((i) => ({ ...i, isRead: true })));
    if (token) {
      try {
        await api.markAllNotificationsRead(token);
      } catch {
        void reload();
      }
    }
  }

  async function dismiss(n: NotificationDto) {
    const token = getAccessToken();
    setItems((prev) => prev.filter((i) => i.id !== n.id));
    if (token) {
      try {
        await api.dismissNotification(token, n.id);
      } catch {
        void reload();
      }
    }
  }

  async function clearAll() {
    const token = getAccessToken();
    setItems([]);
    if (token) {
      try {
        await api.dismissAllNotifications(token);
      } catch {
        void reload();
      }
    }
  }

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)} aria-label="Notifications" title="Notifications" style={bellStyle(open)}>
        <Icon name="bell" size={17} />
        {unread > 0 && <span style={badgeStyle}>{unread}</span>}
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 70 }} />
          <div style={panelStyle}>
            <div style={panelHeader}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>Notifications</span>
              <span style={{ display: "flex", gap: 12 }}>
                {unread > 0 && (
                  <button onClick={() => void markAll()} style={linkBtn}>
                    Mark all read
                  </button>
                )}
                {items.length > 0 && (
                  <button onClick={() => void clearAll()} style={linkBtn}>
                    Clear all
                  </button>
                )}
              </span>
            </div>
            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              {items.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--text-faint)", fontSize: 13.5 }}>You're all caught up.</div>
              ) : (
                items.map((n) => (
                  <div key={n.id} className="kd-row-hover" style={rowStyle}>
                    <button onClick={() => void openItem(n)} style={rowMainStyle}>
                      <span style={{ width: 8, flexShrink: 0, display: "flex", justifyContent: "center", paddingTop: 6 }}>
                        {!n.isRead && <span style={dotStyle} />}
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 13.5, fontWeight: n.isRead ? 500 : 700, color: "var(--text)" }}>{n.summary}</span>
                        <span style={{ display: "block", fontSize: 12, color: "var(--text-faint)" }}>{timeAgo(n.createdAtUtc)}</span>
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        void dismiss(n);
                      }}
                      aria-label="Done"
                      title="Done"
                      style={doneBtn}
                    >
                      <Icon name="check" size={15} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const bellStyle = (active: boolean): CSSProperties => ({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 38,
  width: 38,
  borderRadius: "var(--radius-sm)",
  border: `var(--border-w) solid ${active ? "var(--accent-border)" : "var(--header-btn-border)"}`,
  background: active ? "var(--accent-soft)" : "var(--header-btn-bg)",
  color: active ? "var(--accent-text)" : "var(--header-fg)",
  cursor: "pointer",
});

const badgeStyle: CSSProperties = {
  position: "absolute",
  top: -5,
  right: -5,
  minWidth: 18,
  height: 18,
  padding: "0 4px",
  borderRadius: 99,
  background: "var(--st-overdue)",
  color: "#fff",
  fontSize: 10.5,
  fontWeight: 800,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const panelStyle: CSSProperties = {
  position: "absolute",
  right: 0,
  top: "calc(100% + 8px)",
  zIndex: 80,
  width: 340,
  background: "var(--surface)",
  border: "var(--border-w) solid var(--border)",
  borderRadius: "var(--radius)",
  boxShadow: "var(--shadow-lg)",
  overflow: "hidden",
};

const panelHeader: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "11px 14px",
  borderBottom: "var(--border-w) solid var(--border)",
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  padding: "0 10px 0 0",
};

const rowMainStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  flex: 1,
  minWidth: 0,
  textAlign: "left",
  padding: "10px 0 10px 14px",
  border: "none",
  background: "none",
  cursor: "pointer",
  font: "inherit",
};

const doneBtn: CSSProperties = {
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-faint)",
  cursor: "pointer",
};

const dotStyle: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 99,
  background: "var(--accent)",
  display: "block",
};

const linkBtn: CSSProperties = {
  font: "inherit",
  fontSize: 12.5,
  fontWeight: 600,
  padding: 0,
  border: "none",
  background: "none",
  color: "var(--accent-text)",
  cursor: "pointer",
};
